import { applicationLogger } from "../../logging/logger";
import { autoinject, observable, TaskQueue } from 'aurelia-framework';
import { AdminApi } from "../../services/admin-api";
import { MdCollapsible, MdToastService } from "aurelia-materialize-bridge";
import { OpenIdConnect } from "aurelia-open-id-connect";
import { Page } from "../common/abstract-page";
import { Router } from "aurelia-router";
import { Tenant } from "../../models/tenant";
import { TenantService } from "../../services/tenant-service";
import { TransactionBatch } from "../../models/transaction-batch";
import * as moment from "moment";

@autoinject
export class Reports extends Page {

    @observable startDate: string;
    @observable endDate: string;
    @observable days: any;

    rangeSelection: any;
    tenants: Array<Tenant>;
    singleTenants: Array<Tenant>;
    resubmittedLogs: Array<string>;
    selectedTenant: Tenant;
    batches: Array<TransactionBatch>;
    selectedBatch: TransactionBatch;

    initialized = false;
    summarySummarize = "1";
    detailsSummarize = "1";

    collapsible: MdCollapsible;

    constructor(adminApi: AdminApi,
        toast: MdToastService,
        router: Router,
        openIdConnect: OpenIdConnect,
        private tenantService: TenantService,
        private taskQueue: TaskQueue) {

        super(adminApi, toast, router, openIdConnect);

        this.rangeSelection = [
            { name: "Today", value: 0 },
            { name: "This week", value: -6 + moment().day() },
            { name: "7 days", value: -6 },
            { name: "30 days", value: -29 },
            { name: "MTD", value: -moment().date() + 1 },
            { name: "365 days", value: -364 },
            { name: "YTD", value: -moment().dayOfYear() + 1 }
        ];

        this.days = this.rangeSelection[0];
        this.initialized = true;
    }

    async activate(params: any) {
        this.tenantService.tenantId = params.tenantId ? params.tenantId : "";
        this.tenantService.tenantName = "";

        const response = await this.adminApi.getTenants();
        if (response.success && response.data) {
          this.tenants = response.data.sort();
          this.singleTenants = [...this.tenants];

            if (params.tenantId) {
                this.selectedTenant = (this.tenants.find((tenant): boolean => tenant.id === params.tenantId)) as Tenant;
                this.tenantService.tenantName = this.selectedTenant.name;
            }

            if (this.applicationUserIsGlobalAdministrator) {
                const allTenants = new Tenant();
                allTenants.name = "(All customers)";
                allTenants.id = null;
                this.tenants.unshift(allTenants);
                this.selectedTenant = allTenants;
            } else if (this.tenants.length > 0) {
                this.selectedTenant = this.tenants[0];
            }

            this.tenantChanged();
            this.startDateChanged();
            this.endDateChanged();
        } else {
            applicationLogger.error(response.message);
        }

        this.taskQueue.queueTask(() => this.collapsible.open());
    }

    daysChanged() {
        const end = moment().hours(23).minutes(59).seconds(59);
        this.endDate = end.format("YYYY-MM-DD HH:mm:ss"); 
        const start = moment().hours(0).minutes(0).seconds(0).add(moment.duration(this.days.value, "day"));
        this.startDate = start.format("YYYY-MM-DD HH:mm:ss"); 
    }

    startDateChanged() {
        if (this.initialized) {
            this.startDate = moment(this.startDate).hours(0).minutes(0).seconds(0).format("YYYY-MM-DD HH:mm:ss");
        }
    }

    endDateChanged() {
        if (this.initialized) {
            this.endDate = moment(this.endDate).hours(23).minutes(59).seconds(59).format("YYYY-MM-DD HH:mm:ss");
        }
    }

    tenantChanged() {
        this.tenantService.tenantId = this.selectedTenant.id;
        this.tenantService.tenantName = this.selectedTenant.id ? this.selectedTenant.name : "";
    }

    async reprocessedTenantChanged() {
        this.tenantChanged();

        this.resubmittedLogs = null;
        const response = await this.adminApi.enumerateResubmitLogs(this.selectedTenant.id);
        if (response.success && response.data) {
            this.resubmittedLogs = response.data;
        } else {
            applicationLogger.error(response.message);
        }
    }

    async declinedTenantChanged() {
        this.tenantChanged();

        this.resubmittedLogs = null;
        const response = await this.adminApi.getTransactionBatches(this.selectedTenant.id);
        if (response.success && response.data) {
            this.batches = response.data;
        } else {
            applicationLogger.error(response.message);
        }
    }

    reportChanged(targetElement) {
    }

    async downloadSummaryReport(format: string) {
        await this.adminApi.downloadSummaryReport(format, this.startDate, this.endDate, this.summarySummarize, this.selectedTenant ? this.selectedTenant.id : null);
    }

    async downloadReprocessedReport(logName: string) {
        await this.adminApi.downloadReprocessedReport(this.selectedTenant.id, this.selectedTenant.name, logName);
    }

    async downloadDeclinedReport(format: string) {
        await this.adminApi.downloadDeclinedReport(format, this.selectedBatch);
    }

    async downloadPurchaseDetailsReport(format: string) {
        await this.adminApi.downloadPurchaseDetailsReport(format, this.startDate, this.endDate, this.detailsSummarize, this.selectedTenant ? this.selectedTenant.id : null);
    }
}